'use client';

import SolarCrownLineBold from '@/components-v2/icons/SolarCrownLineBold';
import Box from '@mui/material/Box';
import { alpha } from '@mui/material/styles';
import useTheme from '@mui/material/styles/useTheme';

import { useTolgee } from '@tolgee/react';
import { createContext, useContext } from 'react';
import toast, { Toaster, type Renderable } from 'react-hot-toast';

type Severity = 'success' | 'error' | 'crown';

type PromiseMessages = {
  loading: Renderable;
  success: Renderable | ((data: any) => Renderable);
  error: Renderable | ((err: any) => Renderable);
};

interface ToastContextType {
  showToast: (tolgeeKey: string, severity?: Severity, params?: any) => void;
  showPromiseToast: (promise: Promise<any>, messages: PromiseMessages) => Promise<any>;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

const createShowToast =
  (theme: any, tolgee: any) =>
  (tolgeeKey: string, severity: Severity = 'success', params?: any) => {
    const translatedMessage = tolgee.t(tolgeeKey, params);
    switch (severity) {
      case 'success':
        toast.success(translatedMessage);
        break;
      case 'error':
        toast.error(translatedMessage);
        break;
      case 'crown':
        toast(translatedMessage, {
          icon: <Box component={SolarCrownLineBold} width={20} sx={{ color: 'secondary.main' }} />,
          style: {
            border: `4px solid ${alpha(theme.palette.secondary.main, 0.75)}`,
          },
        });
        break;
    }
  };

const showPromiseToast = (promise: Promise<any>, messages: PromiseMessages) => {
  return toast.promise(promise, messages);
};

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const theme = useTheme();
  const tolgee = useTolgee();
  const showToast = createShowToast(theme, tolgee);

  return (
    <ToastContext.Provider value={{ showToast, showPromiseToast }}>
      <Toaster
        position="bottom-center"
        toastOptions={{
          duration: 4000,
          style: {
            width: 'fit-content',
            maxWidth: '800px',
            padding: '6px 12px',
            borderRadius: '24px',
            border: `4px solid ${alpha(theme.palette.primary.main, 0.5)}`,
          },
          success: {
            style: {
              border: `4px solid ${alpha(theme.palette.success.main, 0.5)}`,
            },
          },
          error: {
            style: {
              border: `4px solid ${alpha(theme.palette.error.main, 0.5)}`,
            },
          },
        }}
      />
      {children}
    </ToastContext.Provider>
  );
}

// Custom hook to use toast
export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}
